/*
 * Pesquisa NPS de evento (ABRA-370) — uma pesquisa por evento, respondida no site por um link
 * individual que segue no e-mail do certificado (#LINK_PESQUISA).
 *
 * As métricas do relatório têm o mesmo formato das pesquisas de curso, então os tipos de lá são
 * reaproveitados em vez de copiados.
 */
import { ISurveyAnswer, ISurveyNps, ISurveyQuestion, ISurveyScaleQuestion } from '../Survey/types'

/** Escala é sempre 0 a 10; só os rótulos das pontas são editáveis. */
export type TEventSurveyQuestionType = 'scale' | 'text'

export const EVENT_SURVEY_SCALE_MIN = 0

export const EVENT_SURVEY_SCALE_MAX = 10

/** Variável que a API e o site trocam pelo nome do evento nos enunciados. */
export const EVENT_NAME_VARIABLE = '#NOME_EVENTO'

export interface IEventSurveyQuestion {
  /** Nulo na pergunta que ainda não foi salva — inclusive as do modelo padrão. */
  id: number | null
  type: TEventSurveyQuestionType
  /** Cru, com #NOME_EVENTO: a troca é feita na leitura, no site e no relatório. */
  statement: string
  short_label: string
  min_label: string | null
  max_label: string | null
  is_required: boolean
  is_nps: boolean
  position: number
}

export interface IEventSurveyConfig {
  /** Nulo enquanto o evento não tem pesquisa: as perguntas vêm do modelo padrão. */
  survey: { id: number; is_active: boolean; title: string; updated_at: string } | null
  event: { id: number; name: string; slug: string; ends_at: string | null }
  has_responses: boolean
  responses_count: number
  /** Perguntas que já têm respostas: não podem ser removidas nem mudar de tipo. */
  locked_question_ids: number[]
  questions: IEventSurveyQuestion[]
}

export interface IEventSurveyQuestionPayload {
  id: number | null
  type: TEventSurveyQuestionType
  statement: string
  short_label: string
  min_label: string | null
  max_label: string | null
  is_required: boolean
  is_nps: boolean
}

/** A ordem do array é a posição das perguntas no formulário. */
export interface IEventSurveyPayload {
  is_active: boolean
  questions: IEventSurveyQuestionPayload[]
}

/** Identificação do evento nas telas da pesquisa — não exige view_events, como o detalhe. */
export interface IEventSurveyEvent {
  id: number
  name: string
  slug: string
  thumb: string | null
  thumb_wide: string | null
}

export interface IEventSurveyInvitations {
  /** Participantes que receberam o link por e-mail, com ou sem certificado. */
  invited: number
  responded: number
  /** Percentual (0–100). Nulo quando ninguém foi convidado ainda. */
  response_rate: number | null
}

export interface IEventSurveyReport {
  survey: { id: number; is_active: boolean; title: string }
  event: { id: number; name: string; slug: string }
  total_responses: number
  invitations: IEventSurveyInvitations
  /** Enunciados já com o nome do evento. */
  questions: ISurveyQuestion[]
  nps: ISurveyNps | null
  scale_questions: ISurveyScaleQuestion[]
}

export interface IEventSurveyResponseRow {
  id: number
  respondent_name: string | null
  respondent_email: string | null
  created_at: string
  /** Pergunta opcional deixada em branco não aparece na lista. */
  answers: ISurveyAnswer[]
}

export interface IEventSurveyResponsesPage {
  current_page: number
  per_page: number
  /** Total da pesquisa sem a busca, para a tela mostrar o quanto ela recortou. */
  total: number
  total_filtered: number
  data: IEventSurveyResponseRow[]
}

export type TEventSurveyDeliveryMode = 'initial' | 'reminder'

export interface IEventSurveyDeliveryPreview {
  survey_active: boolean
  can_send_now: boolean
  opens_at: string | null
  /** Sintético (`.../pesquisa-evento/SEU-LINK-INDIVIDUAL`), só para o exemplo: não é o link de ninguém. */
  example_url: string
  participants: number
  eligible: number
  missing_email: number
  initial: number
  reminder: number
  answered: number
  recently_sent: number
}

export interface IEventSurveyDispatchPayload {
  mode: TEventSurveyDeliveryMode
  subject: string
  body: string
}

export interface IEventSurveyDispatchResult extends IEventSurveyDeliveryPreview {
  dispatched: number
  mode: TEventSurveyDeliveryMode
}
